import React from 'react';

interface LandBuildingSizeInputProps {
    value?: { landSize?: number; buildingSize?: number };
    onChange?: (value: { landSize?: number; buildingSize?: number }) => void;
    landPlaceholder?: string;
    buildingPlaceholder?: string;
}

const LandBuildingSizeInput: React.FC<LandBuildingSizeInputProps> = ({
    value = {},
    onChange,
    landPlaceholder = "Land size",
    buildingPlaceholder = "Building Size"
}) => {
    const [landSize, setLandSize] = React.useState<string>(value?.landSize?.toString() || '');
    const [buildingSize, setBuildingSize] = React.useState<string>(value?.buildingSize?.toString() || '');
    const [isFocused, setIsFocused] = React.useState(false);

    const addDots = (num: string): string => {
        if (!num) return '';
        const removeNonNumeric = num.replace(/[^0-9]/g, '');
        if (removeNonNumeric === '0') return '0';
        return removeNonNumeric.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    const removeDots = (num: string): string => {
        return num.replace(/\./g, '');
    };

    const triggerChange = (land: string, building: string) => {
        const landValue = land ? Number(removeDots(land)) : undefined;
        const buildingValue = building ? Number(removeDots(building)) : undefined;
        onChange?.({
            landSize: landValue,
            buildingSize: buildingValue
        });
    };

    const handleLandChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const formattedValue = addDots(inputValue);
        setLandSize(formattedValue);
        triggerChange(formattedValue, buildingSize);
    };

    const handleBuildingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const formattedValue = addDots(inputValue);
        setBuildingSize(formattedValue);
        triggerChange(landSize, formattedValue);
    };

    React.useEffect(() => {
        const formatLandSize = value?.landSize !== undefined && value?.landSize !== null 
            ? addDots(value.landSize.toString()) 
            : '';
        const formatBuildingSize = value?.buildingSize !== undefined && value?.buildingSize !== null 
            ? addDots(value.buildingSize.toString()) 
            : '';
        
        setLandSize(formatLandSize);
        setBuildingSize(formatBuildingSize);
    }, [value]);

    const containerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        border: '1px solid #d9d9d9',
        borderColor: isFocused ? 'black' : '#d9d9d9',
        borderRadius: '8px',
        padding: '3px 11px',
        background: '#fff',
        outline: 0,
        boxShadow: isFocused ? '0 0 0 2px #d9d9d9' : 'none',
        transition: 'all 0.2s',
        boxSizing: 'border-box',
    };

    const inputStyle: React.CSSProperties = {
        border: 'none',
        outline: 'none',
        background: 'transparent',
        fontSize: '14px',

        flex: 1,
        minWidth: 0,
        padding: 0,
        width: '100%',
        height: '32px',
    };

    const separatorStyle: React.CSSProperties = {
        color: 'black',
        padding: '0 8px',
        userSelect: 'none',
        flexShrink: 0,
        fontSize: '16px',
    };

    const suffixStyle: React.CSSProperties = {
        color: '#9FACBF',
        paddingLeft: '8px',
        userSelect: 'none',
        flexShrink: 0,
        fontSize: '14px',
    };

    return (
        <>
            <style>{`
                .land-building-input-wrapper input::placeholder {
                    color: #bfbfbf;
                }
                .land-building-input-wrapper:hover {
                    border-color: black !important;
                }
            `}</style>
            <div
                className="land-building-input-wrapper"
                style={containerStyle}
                tabIndex={0}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
            >
                <input
                    type="text"
                    placeholder={landPlaceholder}
                    value={landSize}
                    onChange={handleLandChange}
                    style={inputStyle}
                    inputMode="numeric"
                />
                <span style={separatorStyle}>/</span>
                <input
                    type="text"
                    placeholder={buildingPlaceholder}
                    value={buildingSize}
                    onChange={handleBuildingChange}
                    style={inputStyle}
                    inputMode="numeric"
                />
                <span style={suffixStyle}>M²</span>
            </div>
        </>
    );
};

export default LandBuildingSizeInput;